import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { CDNImage } from "~/components/ui/cdn-image";
import { cn } from "~/lib/cn";
import { requirePermission } from "~/lib/functions/admin-access";
import { permissionMiddleware } from "~/lib/permission-middleware";

/**
 * Admin session detail page - shows session info with registration count.
 * Designed to be shared with workshop hosts.
 * Search: admin_session_detail, session_registrations
 */

interface SessionDetail {
  id: string;
  title: string;
  description: string;
  sessionFormat: string;
  startTimeUtc: Date;
  durationMinutes: number;
  speaker: {
    name: string;
    title: string;
    avatarUrl: string | null;
  };
  dayTitle: string;
  dayDate: Date;
  summitTitle: string;
  registrationCount: number;
}

export const getSessionDetail = createServerFn({ method: "GET" })
  .middleware([permissionMiddleware("summit:read")])
  .validator(z.object({ sessionId: z.string().uuid() }))
  .handler(async ({ data }): Promise<SessionDetail | null> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { summitSessionRegistrations } = await import("~/../drizzle/schema");
    const { eq, count } = await import("drizzle-orm");

    const db = await getDBWithInit();

    // Get session with all related data
    const session = await db.query.summitSessions.findFirst({
      where: { id: data.sessionId },
      with: {
        speaker: true,
        day: true,
        summit: true,
      },
    });

    if (!session || !session.speaker || !session.day || !session.summit) {
      return null;
    }

    // Get registration count
    const [countResult] = await db
      .select({ value: count() })
      .from(summitSessionRegistrations)
      .where(eq(summitSessionRegistrations.sessionId, data.sessionId));

    const registrationCount = countResult?.value ?? 0;

    return {
      id: session.id,
      title: session.title,
      description: session.description,
      sessionFormat: session.sessionFormat,
      startTimeUtc: session.startTimeUtc,
      durationMinutes: session.durationMinutes,
      speaker: {
        name: session.speaker.name,
        title: session.speaker.title,
        avatarUrl: session.speaker.avatarUrl,
      },
      dayTitle: session.day.title,
      dayDate: session.day.date,
      summitTitle: session.summit.title,
      registrationCount,
    };
  });

export const Route = createFileRoute("/admin/summit-sessions/$sessionId")({
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "summit:read"),
  loader: async ({ params, context }) => {
    const sessionData = await getSessionDetail({
      data: { sessionId: params.sessionId },
    });

    if (!sessionData) {
      throw new Error("Session not found");
    }

    context.queryClient.setQueryData(
      ["admin", "session-detail", params.sessionId],
      sessionData,
    );

    return { sessionId: params.sessionId };
  },
  component: SessionDetailPage,
  errorComponent: () => (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900">Session not found</h1>
        <p className="mt-2 text-gray-600">
          This session may have been removed or doesn't exist.
        </p>
      </div>
    </div>
  ),
});

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

const timeFormatter = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
});

const sessionFormatLabels: Record<string, string> = {
  presentation: "Presentation",
  workshop: "Workshop",
  panel: "Panel",
  "q&a": "Q&A",
  keynote: "Keynote",
  recording: "Recording",
};

function SessionDetailPage() {
  const { sessionId } = Route.useLoaderData();

  const { data: session, isLoading } = useQuery({
    queryKey: ["admin", "session-detail", sessionId],
    queryFn: () => getSessionDetail({ data: { sessionId } }),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading || !session) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  const endTime = new Date(
    new Date(session.startTimeUtc).getTime() +
      session.durationMinutes * 60 * 1000,
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50">
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Summit Header */}
        <div className="mb-8 text-center">
          <p className="text-sm font-medium tracking-wider text-purple-600 uppercase">
            {session.summitTitle}
          </p>
        </div>

        {/* Main Card */}
        <div className="overflow-hidden rounded-2xl bg-white shadow-xl ring-1 shadow-gray-200/50 ring-gray-100">
          {/* Session Type Banner */}
          <div
            className={cn(
              "px-6 py-3 text-center",
              session.sessionFormat === "workshop"
                ? "bg-purple-600"
                : session.sessionFormat === "keynote"
                  ? "bg-amber-500"
                  : "bg-gray-700",
            )}
          >
            <span className="text-sm font-semibold tracking-wider text-white uppercase">
              {sessionFormatLabels[session.sessionFormat] ??
                session.sessionFormat}
            </span>
          </div>

          {/* Content */}
          <div className="px-6 py-8 sm:px-10 sm:py-10">
            {/* Session Title */}
            <h1 className="text-center text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              {session.title}
            </h1>

            {/* Date & Time */}
            <div className="mt-6 flex flex-col items-center gap-1 text-gray-600">
              <p className="font-medium">
                {dateFormatter.format(new Date(session.dayDate))}
              </p>
              <p className="text-sm">
                {timeFormatter.format(new Date(session.startTimeUtc))} –{" "}
                {timeFormatter.format(endTime)}
              </p>
              <p className="mt-1 text-xs text-gray-400">{session.dayTitle}</p>
            </div>

            {/* Speaker */}
            <div className="mt-8 flex flex-col items-center">
              {session.speaker.avatarUrl ? (
                <CDNImage
                  src={session.speaker.avatarUrl}
                  alt={session.speaker.name}
                  width={96}
                  height={96}
                  className="h-24 w-24 rounded-full object-cover shadow-lg ring-4 ring-white"
                />
              ) : (
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-purple-400 to-purple-600 text-2xl font-bold text-white shadow-lg ring-4 ring-white">
                  {session.speaker.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)}
                </div>
              )}
              <h2 className="mt-4 text-xl font-semibold text-gray-900">
                {session.speaker.name}
              </h2>
              <p className="text-sm text-gray-500">{session.speaker.title}</p>
            </div>

            {/* Description */}
            {session.description && (
              <div className="mt-8">
                <p className="text-center leading-relaxed text-gray-600">
                  {session.description}
                </p>
              </div>
            )}
          </div>

          {/* Registration Count - Hero Metric */}
          <div className="border-t border-gray-100 bg-gradient-to-br from-gray-50 to-purple-50 px-6 py-10 sm:px-10">
            <div className="text-center">
              <p className="text-sm font-medium tracking-wider text-gray-500 uppercase">
                Registered Attendees
              </p>
              <p className="mt-3 text-7xl font-bold tracking-tight text-purple-600 sm:text-8xl">
                {session.registrationCount}
              </p>
              <p className="mt-2 text-sm text-gray-400">
                people signed up for this session
              </p>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <p className="mt-8 text-center text-xs text-gray-400">
          Registration data refreshes automatically every 30 seconds
        </p>
      </div>
    </div>
  );
}
